import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  ADMIN_PROSPECTS_PAGE_SIZE,
  buildListAdminProspectImportHistoryRequest,
  listAdminProspectImportHistory,
} from '../../services/admin/prospectsService';
import { createAdminProspectRequestGate } from '../../services/admin/prospectTargetingFilters';
import {
  classifyAdminCallableError,
  adminProspectListErrorKey,
} from './adminProspectDisplay';
import {
  buildImportHistoryRecordedRows,
  formatImportHistoryImportedAt,
  formatImportHistorySource,
  importHistoryActionLabel,
  importHistoryChangedFieldsSummary,
  mergeImportHistoryEntries,
} from './prospectImportHistoryDisplay';
import { isProspectLazyPanelVisible, shouldMountProspectLazyPanel } from './adminProspectDetailMobileSections';

/**
 * Read-only import history disclosure for Immo 360.
 * Lazy-loads on first open; resets when prospectId changes.
 */
export default function ProspectImportHistorySection({
  prospectId,
  language,
  t,
  mobileLayout = false,
  mobileActive = false,
  hideTitle = false,
  embedded = false,
}) {
  const id = String(prospectId || '').trim();
  const sectionId = useId();
  const panelId = `${sectionId}-panel`;
  const buttonId = `${sectionId}-toggle`;

  const [open, setOpen] = useState(false);
  const [mobileDisclosedOnce, setMobileDisclosedOnce] = useState(false);
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState('idle');
  const [errorKind, setErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [orderingCoverage, setOrderingCoverage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  const gateRef = useRef(createAdminProspectRequestGate());
  const mountedRef = useRef(true);
  const prospectIdRef = useRef(id);
  const loadingMoreRef = useRef(false);

  prospectIdRef.current = id;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    gateRef.current.next();
    setOpen(false);
    setMobileDisclosedOnce(false);
    setEntries([]);
    setStatus('idle');
    setErrorKind(null);
    setNextPageToken(null);
    setOrderingCoverage(null);
    setBusy(false);
    setExpandedIds(new Set());
    setLoadMoreFailed(false);
    loadingMoreRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!mobileLayout) return;
    if (!mobileActive) return;
    setMobileDisclosedOnce(true);
  }, [mobileLayout, mobileActive]);

  useEffect(() => {
    if (!mobileLayout && mobileDisclosedOnce && status !== 'idle') {
      setOpen(true);
    }
  }, [mobileLayout, mobileDisclosedOnce, status]);

  const loadHistory = useCallback(
    async ({ append = false, pageToken } = {}) => {
      if (!id) return;
      if (append && loadingMoreRef.current) return;
      const ownerProspectId = id;
      const requestId = gateRef.current.next();
      if (append) loadingMoreRef.current = true;
      setBusy(true);
      if (!append) {
        setStatus('loading');
        setErrorKind(null);
        setLoadMoreFailed(false);
      }
      try {
        const data = await listAdminProspectImportHistory(
          buildListAdminProspectImportHistoryRequest({
            prospectId: ownerProspectId,
            pageSize: ADMIN_PROSPECTS_PAGE_SIZE,
            pageToken,
          }),
        );
        if (
          !mountedRef.current ||
          !gateRef.current.isCurrent(requestId) ||
          prospectIdRef.current !== ownerProspectId
        ) {
          return;
        }
        const rows = Array.isArray(data?.entries) ? data.entries : [];
        setEntries((current) => (append ? mergeImportHistoryEntries(current, rows) : rows));
        setNextPageToken(data?.nextPageToken || null);
        setOrderingCoverage(data?.orderingCoverage || null);
        setLoadMoreFailed(false);
        if (append) setStatus('ready');
        else setStatus(rows.length === 0 ? 'empty' : 'ready');
      } catch (err) {
        if (
          !mountedRef.current ||
          !gateRef.current.isCurrent(requestId) ||
          prospectIdRef.current !== ownerProspectId
        ) {
          return;
        }
        setErrorKind(classifyAdminCallableError(err));
        if (append) {
          setLoadMoreFailed(true);
          setStatus('ready');
        } else {
          setEntries([]);
          setNextPageToken(null);
          setStatus('error');
        }
      } finally {
        if (append) loadingMoreRef.current = false;
        if (
          mountedRef.current &&
          gateRef.current.isCurrent(requestId) &&
          prospectIdRef.current === ownerProspectId
        ) {
          setBusy(false);
        }
      }
    },
    [id],
  );

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && status === 'idle') {
      loadHistory();
    }
  };

  useEffect(() => {
    if (!mobileLayout || !mobileActive) return;
    if (status === 'idle') {
      loadHistory();
    }
  }, [mobileLayout, mobileActive, status, loadHistory]);

  const toggleExpanded = (entryId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const errorMessage = errorKind ? t(adminProspectListErrorKey(errorKind)) : t('admin.prospection.error.generic');
  const showInternalToggle = !mobileLayout;
  const keepMounted = shouldMountProspectLazyPanel({
    mobileLayout,
    mobileActive,
    mobileDisclosedOnce,
    open,
    status,
  });
  const panelVisible = isProspectLazyPanelVisible({
    mobileLayout,
    mobileActive,
    mobileDisclosedOnce,
    open,
    status,
  });
  const chrome = embedded ? 'min-w-0' : 'rounded-xl border border-slate-200 bg-white p-4';

  return (
    <section className={chrome} data-embedded={embedded ? 'true' : 'false'}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {!hideTitle ? (
            <h2 className="text-sm font-semibold text-slate-800">
              {t('admin.prospection.importHistory.title')}
            </h2>
          ) : null}
          <p className={`${hideTitle ? 'mt-0' : 'mt-1'} text-xs text-slate-500`}>
            {t('admin.prospection.importHistory.subtitle')}
          </p>
        </div>
        {showInternalToggle ? (
          <button
            id={buttonId}
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={handleToggle}
            className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {open ? t('admin.prospection.importHistory.hide') : t('admin.prospection.importHistory.show')}
          </button>
        ) : null}
      </div>

      <div
        id={panelId}
        role="region"
        aria-labelledby={showInternalToggle ? buttonId : undefined}
        className={panelVisible ? 'mt-4' : 'hidden'}
        hidden={!panelVisible}
      >
        {keepMounted || panelVisible ? (
          <>
          {status === 'loading' ? (
            <p className="text-sm text-slate-500" aria-live="polite">
              {t('admin.prospection.importHistory.loading')}
            </p>
          ) : null}

          {status === 'empty' ? (
            <>
              <p className="text-sm text-slate-600">{t('admin.prospection.importHistory.empty')}</p>
              {orderingCoverage === 'importedAt_present_only' ? (
                <p className="mt-2 text-xs text-slate-500">
                  {t('admin.prospection.importHistory.coverage.importedAt')}
                </p>
              ) : null}
            </>
          ) : null}

          {status === 'error' ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p>{errorMessage}</p>
              <button
                type="button"
                onClick={() => loadHistory()}
                className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {t('admin.prospection.retry')}
              </button>
            </div>
          ) : null}

          {entries.length > 0 && (status === 'ready' || loadMoreFailed) ? (
            <>
              <p className="mb-3 text-xs text-slate-500">{t('admin.prospection.importHistory.disclaimer')}</p>
              {orderingCoverage === 'importedAt_present_only' ? (
                <p className="mb-3 text-xs text-slate-500">
                  {t('admin.prospection.importHistory.coverage.importedAt')}
                </p>
              ) : null}
              <ul className="space-y-3">
                {entries.map((entry) => {
                  const expanded = expandedIds.has(entry.id);
                  const detailsId = `${sectionId}-details-${entry.id}`;
                  const recordedRows = expanded
                    ? buildImportHistoryRecordedRows(entry, language, t)
                    : [];
                  return (
                    <li
                      key={entry.id}
                      className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 p-3"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            {formatImportHistoryImportedAt(entry.importedAt, language)}
                          </p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">
                            {importHistoryActionLabel(entry.actionKind, t)}
                            {' · '}
                            {formatImportHistorySource(entry.sourceType, t)}
                          </p>
                          <p className="mt-1 break-words text-sm text-slate-700">
                            {importHistoryChangedFieldsSummary(entry.changedFields, t)}
                          </p>
                          {entry.schemaPartial ? (
                            <p className="mt-1 text-xs text-amber-700">
                              {t('admin.prospection.importHistory.partial')}
                            </p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          aria-expanded={expanded}
                          aria-controls={detailsId}
                          onClick={() => toggleExpanded(entry.id)}
                          className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {expanded
                            ? t('admin.prospection.importHistory.collapse')
                            : t('admin.prospection.importHistory.expand')}
                        </button>
                      </div>
                      {expanded ? (
                        <div id={detailsId} className="mt-3 space-y-3 border-t border-slate-200 pt-3">
                          <p className="text-xs text-slate-500">
                            {t('admin.prospection.importHistory.recordedHint')}
                          </p>
                          {recordedRows.length === 0 ? (
                            <p className="text-sm text-slate-600">
                              {t('admin.prospection.importHistory.snapshot.empty')}
                            </p>
                          ) : (
                            recordedRows.map((section) => (
                              <div key={section.key} className="min-w-0">
                                <div className="flex flex-wrap items-baseline gap-2">
                                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                    {section.title}
                                  </h3>
                                  <span className="text-xs text-slate-500">{section.application}</span>
                                </div>
                                <dl className="mt-2 grid gap-2 sm:grid-cols-2">
                                  {section.fields.map((field) => (
                                    <div key={`${section.key}-${field.label}`} className="min-w-0">
                                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                        {field.label}
                                      </dt>
                                      <dd className="mt-0.5 break-words text-sm text-slate-900">
                                        {field.value}
                                      </dd>
                                    </div>
                                  ))}
                                </dl>
                              </div>
                            ))
                          )}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}

          {loadMoreFailed ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p>{t('admin.prospection.importHistory.loadMoreError')}</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => loadHistory({ append: true, pageToken: nextPageToken })}
                className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60"
              >
                {t('admin.prospection.retry')}
              </button>
            </div>
          ) : null}

          {nextPageToken && status === 'ready' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => loadHistory({ append: true, pageToken: nextPageToken })}
              className="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.importHistory.more')}
            </button>
          ) : null}
          </>
        ) : null}
        </div>
    </section>
  );
}
